import { useEffect, useCallback, useRef, useState } from 'react';

interface UseUnsavedChangesGuardOptions {
  isDirty: boolean;
  message?: string;
}

/**
 * Hook to warn user about unsaved changes.
 * Works with classic BrowserRouter (no data router required).
 * 
 * - Handles browser refresh/close via beforeunload
 * - Returns helpers for internal navigation confirmation
 */
export function useUnsavedChangesGuard({ 
  isDirty, 
  message = 'Du har osparade ändringar. Vill du verkligen lämna sidan?' 
}: UseUnsavedChangesGuardOptions) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const pendingNavigationRef = useRef<(() => void) | null>(null);

  // Handle browser back/forward and tab close
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    if (isDirty) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDirty]);

  /**
   * Request navigation - shows confirm dialog if there are unsaved changes
   */
  const requestNavigate = useCallback((navigateFn: () => void) => {
    if (!isDirty) {
      navigateFn();
      return;
    }
    
    pendingNavigationRef.current = navigateFn;
    setConfirmOpen(true);
  }, [isDirty]);

  /**
   * Confirm leaving - executes pending navigation
   */
  const confirmNavigation = useCallback(() => {
    setConfirmOpen(false);
    if (pendingNavigationRef.current) {
      pendingNavigationRef.current();
      pendingNavigationRef.current = null;
    }
  }, []);

  /**
   * Cancel leaving - closes dialog without navigating
   */
  const cancelNavigation = useCallback(() => {
    setConfirmOpen(false);
    pendingNavigationRef.current = null;
  }, []);

  return {
    /** Whether the confirm dialog should be shown */
    isConfirmOpen: confirmOpen,
    /** Call this to request navigation (will prompt if dirty) */
    requestNavigate,
    /** Call this when user confirms they want to leave */
    confirmNavigation,
    /** Call this when user wants to stay */
    cancelNavigation,
    /** The message to show in the dialog */
    message,
  };
}
